/**
 * Unpacks a dropped Letterboxd export — the `.zip` from Letterboxd's
 * "Settings → Import & Export → Export your data", or a single loose `.csv`
 * — entirely client-side via `fflate`. Nothing here ever leaves the browser;
 * the file is read with the File API and unzipped in memory.
 *
 * Letterboxd's zip nests everything under a dated folder
 * (`letterboxd-export-2026-08-03/watched.csv`), so files are located by
 * basename, case-insensitively, ignoring whatever directory they're under.
 */

import { unzipSync } from "fflate";

export type LetterboxdFileKind = "diary" | "watched" | "ratings";

export interface LetterboxdExportFiles {
  diary: string | null;
  watched: string | null;
  ratings: string | null;
}

function basename(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] ?? path;
}

function kindFromFilename(path: string): LetterboxdFileKind | null {
  switch (basename(path).toLowerCase()) {
    case "diary.csv":
      return "diary";
    case "watched.csv":
      return "watched";
    case "ratings.csv":
      return "ratings";
    default:
      return null;
  }
}

/**
 * A bare `.csv` (no zip) carries no folder context, so when its own filename
 * doesn't match one of the three known names, fall back to sniffing the
 * header row: `diary.csv` is the only one with a "Rewatch"/"Watched Date"
 * column, `ratings.csv` the only one with "Rating" but no "Rewatch".
 */
function kindFromHeader(text: string): LetterboxdFileKind {
  const header = (text.split(/\r\n|\r|\n/, 1)[0] ?? "").toLowerCase();
  if (header.includes("rewatch") || header.includes("watched date")) return "diary";
  if (header.includes("rating")) return "ratings";
  return "watched";
}

const decoder = new TextDecoder("utf-8");

function extractFromZip(buffer: Uint8Array): LetterboxdExportFiles {
  const result: LetterboxdExportFiles = { diary: null, watched: null, ratings: null };
  const unzipped = unzipSync(buffer);
  for (const [path, bytes] of Object.entries(unzipped)) {
    if (path.endsWith("/")) continue; // directory entry
    const kind = kindFromFilename(path);
    if (kind) result[kind] = decoder.decode(bytes);
  }
  return result;
}

/**
 * Reads the dropped file and returns the raw text of whichever of
 * diary.csv / watched.csv / ratings.csv it contains. A `.zip` may supply all
 * three; a bare `.csv` supplies exactly one. Missing files stay `null` — the
 * caller decides whether that's fatal (no files at all) or fine (e.g. no
 * ratings.csv, which not every export includes).
 */
export async function unpackLetterboxdExport(file: File | Blob): Promise<LetterboxdExportFiles> {
  const name = file instanceof File ? file.name : "";
  if (name.toLowerCase().endsWith(".zip")) {
    const buffer = new Uint8Array(await file.arrayBuffer());
    return extractFromZip(buffer);
  }

  const text = await file.text();
  const result: LetterboxdExportFiles = { diary: null, watched: null, ratings: null };
  result[kindFromFilename(name) ?? kindFromHeader(text)] = text;
  return result;
}
